import NightOrder from "../classes/NightOrder.js";
import InPlayCounter from "../classes/InPlayCounter.js";
import Pad from "../classes/Pad.js";
import {
    lookupOne,
    lookupOneCached,
    announceInput,
} from "../utils/elements.js";
import {
    debounce,
} from "../utils/functions.js";
import {
    gameObserver,
    tokenObserver
} from "../constants/observers.js";
import {
    isSpecialTeam
} from "../constants/teams.js";

const nightOrder = new NightOrder();
const pad = lookupOneCached(".js--pad").pad;

nightOrder.setHolders({
    first: lookupOneCached("#first-night"),
    other: lookupOneCached("#other-nights")
});

gameObserver.on("characters-selected", ({ detail }) => {

    nightOrder.reset();
    nightOrder.setCustomOrder(detail.meta);
    nightOrder.setCharacters(
        detail.characters
            .filter((character) => !isSpecialTeam(character.getTeam()))
    );
    nightOrder.drawAllNightOrders();

});

// #145 - Show the "First Night" order after clearing the grimoire.
gameObserver.on("clear", () => {
    lookupOneCached(".js--night-order--carousel").scrollLeft = 0;
});

// #171 - Keep track of the visible night order between refreshes.
const carousel = lookupOneCached(".js--night-order--carousel");
const carouselParent = carousel.parentElement;
const nightOrderCheckbox = lookupOneCached("#night-order-swiped");
carousel.addEventListener("scroll", debounce(({ target }) => {

    const wasChecked = nightOrderCheckbox.checked;

    nightOrderCheckbox.checked = (
        target.scrollLeft === carouselParent.offsetWidth
    );

    if (nightOrderCheckbox.checked !== wasChecked) {
        announceInput(nightOrderCheckbox);
    }

}), { passive: true });
nightOrderCheckbox.addEventListener("input", () => {

    carousel.scrollLeft = (
        nightOrderCheckbox.checked
        ? carouselParent.offsetWidth
        : 0
    );

});

// TODO: Travellers and Fabled should be unique, it should only be possible to
// add 1 of each. Add that limitation so we don't need to count them anymore.
const inPlay = new InPlayCounter(tokenObserver);

tokenObserver.on("character-add", ({ detail }) => {

    const {
        character
    } = detail;

    // #155 - If we have a traveller or a fabled, add it to the night order.
    if (isSpecialTeam(character.getTeam())) {

        nightOrder.setCharacter(character);
        nightOrder.placeInOrder(character);

    }

    // #131 - check the character isn't from the previous script.
    if (!nightOrder.hasCharacter(character)) {
        return;
    }

    nightOrder.addCharacter(character);

});

tokenObserver.on("character-remove", ({ detail }) => {

    const {
        character
    } = detail;

    // #155 - If we're removing a fabled or traveller, remove them if necessary.
    if (
        isSpecialTeam(character.getTeam())
        && !inPlay.isInPlay(character.getId())
    ) {
        nightOrder.unsetCharacter(character);
    }

    // #131 - check the character isn't from the previous script.
    if (!nightOrder.hasCharacter(character)) {
        return;
    }

    nightOrder.removePlayerName(character, detail.token);
    nightOrder.removeCharacter(character);

});

tokenObserver.on("shroud-toggle", ({ detail }) => {

    // #131 - check the character isn't from the previous script.
    if (!nightOrder.hasCharacter(detail.character)) {
        return;
    }

    nightOrder.toggleDead(detail.character, detail.isDead);

    if (detail.isDead) {
        nightOrder.removePlayerName(detail.character, detail.token);
    } else {

        nightOrder.setPlayerName(
            detail.character,
            detail.token,
            pad.getPlayerName(detail.character)
        );

    }

});

tokenObserver.on("set-player-name", ({ detail }) => {

    if (!nightOrder.hasCharacter(detail.character) || detail.character.isDead) {
        return;
    }

    nightOrder.setPlayerName(detail.character, detail.token, detail.name);

});

// Update the night order badges on the tokens.
// #72: Use objects rather than arrays to allow for decimals in night orders.
const tokenNightOrder = {
    first: {
        counts: Object.create(null),
        dataKey: "firstNight",
        getOrder: (character) => character.getFirstNight()
    },
    other: {
        counts: Object.create(null),
        dataKey: "otherNight",
        getOrder: (character) => character.getOtherNight()
    }
};

function assignCounts({ counts, dataKey }) {

    let count = 0;

    Object
        .keys(counts)
        .sort((a, b) => Number(a) - Number(b))
        .forEach((key) => {

            count += 1;
            counts[key].forEach(({ token }) => {
                Pad.getToken(token).dataset[dataKey] = count;
            });

        });

}

function updateTokens() {
    Object.values(tokenNightOrder).forEach(assignCounts);
}

tokenObserver.on("character-add", ({ detail }) => {

    const {
        character,
        token
    } = detail;

    Object.values(tokenNightOrder).forEach(({ counts, getOrder }) => {

        const order = getOrder(character);

        if (order) {
            (counts[order] ??= []).push({ character, token });
        }

    });

    updateTokens();

});

tokenObserver.on("character-remove", ({ detail }) => {

    const {
        character,
        token
    } = detail;

    Object.values(tokenNightOrder).forEach(({ counts, getOrder }) => {

        const order = getOrder(character);
        const list = counts[order];

        if (!list) {
            return;
        }

        const index = list.findIndex((info) => info.token === token);

        if (index > -1) {
            list.splice(index, 1);
        }

        if (!list.length) {
            delete counts[order];
        }

    });

    updateTokens();

});

const showDead = lookupOne("#show-dead");

showDead.addEventListener("change", ({ target }) => {

    const showDead = target.checked;

    nightOrder.setShowDead(showDead);
    gameObserver.trigger("night-order-show-dead", {
        showDead
    });

});

lookupOne("#show-all").addEventListener("change", ({ target }) => {

    const showAll = target.checked;

    nightOrder.setShowNotInPlay(showAll);
    gameObserver.trigger("night-order-show-all", {
        showAll
    });

    // Showing all characters not in play but hiding the dead can seem
    // confusing. This forces "show dead" to be true when showing all, although
    // the user can hide the dead separately.
    if (showAll && !showDead.checked) {

        showDead.checked = true;
        announceInput(showDead);

    }

});
